// Real Compliance Calendar page (replaces the Sprint 0 stub).
//
// Top: project picker + 90-day-rolling / All toggle.
// KPI row: overdue, due 7d, in flight, accepted YTD, total tracked.
// Category filter chips: federal financial / labor / materials / etc.
// Center: timeline grouped by week, each obligation shows code, name,
//   category, cadence, due-in-Nd, and status badge.
// Click row → side drawer with citation, evidence info, attachment slot.
//
// Backend: GET /api/bead-pm/projects/:id/obligations[?from&to&category&status]
//          GET /api/bead-pm/projects/:id/compliance-summary

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';
import AutopilotPanel from '../components/AutopilotPanel';
import PushToStatePortalModal from '../components/PushToStatePortalModal';

const DEMO_PROJECT_ID = '85a46d17-50f3-4bcc-be60-68074efcd5c7';

type Obligation = {
  id: string;
  catalog_id: string;
  phase_id: string | null;
  due_date: string;
  period_start: string | null;
  period_end: string | null;
  status: 'pending' | 'in_progress' | 'ai_drafted' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
  notes: string | null;
  // joined from catalog
  code: string;
  name: string;
  category: string;
  category_label: string;
  source_cite: string | null;
  cadence_rule: string;
  evidence_type: string | null;
  owner_role: string | null;
  auto_level: string;
};

type ComplianceSummary = {
  overdue: number;
  due_7d: number;
  in_flight: number;
  accepted_ytd: number;
  rejected: number;
  total: number;
};

const CATEGORY_COLOR: Record<string, string> = {
  federal_financial:     '#3b82f6',
  federal_labor:         '#8b5cf6',
  federal_materials:     '#f59e0b',
  federal_environmental: '#10b981',
  federal_civil_rights:  '#ec4899',
  federal_cybersecurity: '#ef4444',
  state_sar:             '#0891b2',
  state_quarterly:       '#06b6d4',
  service_subscriber:    '#22c55e',
  anchor_institutions:   '#a855f7',
  public_awareness:      '#f97316',
  climate:               '#14b8a6',
  digital_equity:        '#6366f1',
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  ai_drafted:  { bg: '#f3e8ff', fg: '#6b21a8', label: '✦ AI-drafted' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg,
      padding: '3px 8px', borderRadius: 999,
      fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '.04em',
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// Parses dates safely whether the API returns 'YYYY-MM-DD' or a full
// ISO timestamp ('2026-05-04T00:00:00.000Z') or an already-Date object.
// Always treats date-only strings as LOCAL (avoids the off-by-one bug
// where new Date('2026-04-01') in PT renders as March 31).
function parseLocalDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

const fmtDate = (d: any) => {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString() : '\u2014';
};

function daysFromNow(d: any): number {
  const dt = parseLocalDate(d);
  if (!dt) return NaN;
  const ms = dt.getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86_400_000);
}

function groupByWeek(obligations: Obligation[]): Map<string, Obligation[]> {
  const groups = new Map<string, Obligation[]>();
  for (const o of obligations) {
    const d = parseLocalDate(o.due_date);
    if (!d) continue;
    const dayOfWeek = d.getDay() === 0 ? 6 : d.getDay() - 1;  // Mon-anchor
    const monday = new Date(d);
    monday.setDate(d.getDate() - dayOfWeek);
    // Use local YYYY-MM-DD as the map key (avoid toISOString to skip TZ shift)
    const yyyy = monday.getFullYear();
    const mm = String(monday.getMonth() + 1).padStart(2, '0');
    const dd = String(monday.getDate()).padStart(2, '0');
    const key = `${yyyy}-${mm}-${dd}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(o);
  }
  return groups;
}

function weekLabel(monday: string): string {
  const d = parseLocalDate(monday);
  if (!d) return monday;
  const sun = new Date(d);
  sun.setDate(d.getDate() + 6);
  const fmt = (x: Date) => `${x.toLocaleString('en-US', { month: 'short' })} ${x.getDate()}`;
  return `Week of ${fmt(d)} \u2013 ${fmt(sun)}`;
}

function KpiCard({ label, value, tone, sub }: { label: string; value: string; tone?: string; sub?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : 'var(--text)';
  return (
    <div className="card">
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default function CompliancePage() {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(DEMO_PROJECT_ID);
  const [view, setView] = useState<'90' | 'all'>('90');
  const [filter, setFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Obligation | null>(null);
  const [pushOpen, setPushOpen] = useState(false);

  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const ninetyOut = new Date(today.getTime() + 90 * 86_400_000).toISOString().slice(0, 10);
  // Look back 30 days too — keeps recently overdue / in-flight items visible.
  const thirtyBack = new Date(today.getTime() - 30 * 86_400_000).toISOString().slice(0, 10);

  const { data: projects = [] } = useQuery<any[]>({
    queryKey: ['bead-projects-comp'],
    queryFn: () => api.get('/bead/projects'),
  });

  const { data: phasesData } = useQuery<{ phases: any[] }>({
    queryKey: ['pm-phases-for-compliance', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/phases`),
    enabled: !!projectId,
  });
  const phasesById = new Map<string, any>();
  for (const ph of phasesData?.phases ?? []) phasesById.set(ph.id, ph);

  const { data: obligations = [], isLoading } = useQuery<Obligation[]>({
    queryKey: ['obligations', projectId, view],
    queryFn: () => {
      const qs = view === '90' ? `?from=${thirtyBack}&to=${ninetyOut}` : '';
      return api.get(`/bead-pm/projects/${projectId}/obligations${qs}`);
    },
    enabled: !!projectId,
  });

  const { data: summary } = useQuery<ComplianceSummary>({
    queryKey: ['comp-summary', projectId],
    queryFn: () => api.get(`/bead-pm/projects/${projectId}/compliance-summary`),
    enabled: !!projectId,
  });

  // Permits live in their own subsystem with their own status state machine
  // (needed / applied / in_review / issued / denied). When a permit is issued,
  // its parent obligation rolls up to "accepted" automatically. We surface
  // permits as a separate KPI strip here so an operator can see at a glance
  // whether the permit work is the blocker for compliance progress.
  const { data: permitSummary } = useQuery<any>({
    queryKey: ['permits-summary', projectId],
    queryFn: () => api.get(`/permits/summary?project_id=${projectId}`),
    enabled: !!projectId,
  });

  const filtered = useMemo(
    () => filter ? obligations.filter(o => o.category === filter) : obligations,
    [obligations, filter]
  );
  const groups = useMemo(() => groupByWeek(filtered), [filtered]);
  const groupKeys = useMemo(() => Array.from(groups.keys()).sort(), [groups]);

  const categories = useMemo(() => {
    const m = new Map<string, { label: string; count: number }>();
    for (const o of obligations) {
      const c = m.get(o.category);
      if (c) c.count++;
      else m.set(o.category, { label: o.category_label, count: 1 });
    }
    return Array.from(m.entries()).sort((a, b) => a[1].label.localeCompare(b[1].label));
  }, [obligations]);

  const selectedProject = projects.find((p: any) => p.id === projectId);

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Compliance Overview</h1>
          <div className="page__subtitle">
            {selectedProject ? `${selectedProject.project_code} · ${selectedProject.project_name}` : 'Every obligation, every cadence'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 13, minWidth: 220 }}>
            {projects.map((p: any) => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {[
              { v: '90' as const,  l: 'Active (-30/+90d)' },
              { v: 'all' as const, l: 'All' },
            ].map(o => (
              <button key={o.v} onClick={() => setView(o.v)}
                style={{
                  padding: '6px 14px',
                  background: view === o.v ? '#0f172a' : 'white',
                  color: view === o.v ? 'white' : 'var(--text)',
                  border: 0, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                }}>{o.l}</button>
            ))}
          </div>
          <button
            onClick={() => setPushOpen(true)}
            title="Generate every integration payload (POPS, NTIA Performance Measures, SAR 2.0, BABA evidence, Davis-Bacon, NEPA, match, reimbursement) and push to your state's portal."
            style={{
              padding: '6px 14px',
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >▸ Push to State Portal</button>
          <a
            href={`/api/bead-pm/projects/${projectId}/audit-binder`}
            download
            title="ZIP every obligation, BABA cert, BSL evidence, milestone, permit, and audit-log event for this project — what an auditor asks for on Day 1."
            style={{
              padding: '6px 14px',
              background: '#0f172a',
              color: 'white',
              border: '1px solid #0f172a',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >⬇ Generate audit binder</a>
        </div>
      </div>

      {/* ──── Permits sub-strip ──────────────────────────────────────────
          The permit subsystem is the lower layer for the FED-ENV-* obligations.
          Surface its state alongside obligation state so an operator can see
          whether they're stuck on the obligation paperwork or the actual
          underlying permit work. Clicking through goes to /permits.
      */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4, marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Permits
        </div>
        <a href="/permits" style={{ fontSize: 11, color: '#1e3a8a', textDecoration: 'none' }}>Open permits hub →</a>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 14 }}>
        <KpiCard label="Permits total"    value={String(permitSummary?.total ?? 0)} />
        <KpiCard label="Needed"           value={String(permitSummary?.needed ?? 0)} tone={(permitSummary?.needed ?? 0) > 0 ? 'warn' : 'good'} />
        <KpiCard label="In flight"        value={String((Number(permitSummary?.applied ?? 0)) + (Number(permitSummary?.in_review ?? 0)))} />
        <KpiCard label="Issued"           value={String(permitSummary?.issued ?? 0)} tone="good" />
        <KpiCard label="Denied"           value={String(permitSummary?.denied ?? 0)} tone={(permitSummary?.denied ?? 0) > 0 ? 'bad' : undefined} />
        <KpiCard label="Crossings tracked" value={String(permitSummary?.total_crossings ?? 0)} sub="across all master permits" />
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 8, marginBottom: 6 }}>
        Obligations
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Overdue" value={String(summary?.overdue ?? 0)} tone={(summary?.overdue ?? 0) > 0 ? 'bad' : 'good'} />
        <KpiCard label="Due in 7 days" value={String(summary?.due_7d ?? 0)} tone="warn" />
        <KpiCard label="In flight" value={String(summary?.in_flight ?? 0)} />
        <KpiCard label="Accepted YTD" value={String(summary?.accepted_ytd ?? 0)} tone="good" />
        <KpiCard label="Total tracked" value={String(summary?.total ?? 0)} />
      </div>

      {categories.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          <button onClick={() => setFilter(null)}
            style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999,
              border: '1px solid var(--border)',
              background: filter === null ? '#0f172a' : 'white',
              color: filter === null ? 'white' : 'var(--text)',
              cursor: 'pointer',
            }}>
            All ({obligations.length})
          </button>
          {categories.map(([cat, info]) => (
            <button key={cat} onClick={() => setFilter(cat)}
              style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999,
                border: '1px solid var(--border)',
                background: filter === cat ? '#0f172a' : 'white',
                color: filter === cat ? 'white' : 'var(--text)',
                borderLeft: `4px solid ${CATEGORY_COLOR[cat] ?? '#64748b'}`,
                cursor: 'pointer',
              }}>
              {info.label} ({info.count})
            </button>
          ))}
        </div>
      )}

      {isLoading && <div className="card"><div className="card__body" style={{ color: 'var(--text-muted)' }}>Loading obligations…</div></div>}
      {!isLoading && filtered.length === 0 && (
        <div className="card"><div className="card__body" style={{ color: 'var(--text-muted)' }}>
          No obligations in window. {filter ? 'Try clearing the category filter.' : 'Try toggling to All.'}
        </div></div>
      )}

      {groupKeys.map(weekStart => (
        <div key={weekStart} className="card" style={{ marginBottom: 12 }}>
          <div style={{
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
            background: 'var(--bg-subtle, #f8fafc)',
            fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '.05em',
          }}>
            {weekLabel(weekStart)}
          </div>
          <div>
            {groups.get(weekStart)!
              .sort((a, b) => a.due_date.localeCompare(b.due_date))
              .map(o => {
                const days = daysFromNow(o.due_date);
                return (
                  <div key={o.id} onClick={() => setSelected(o)}
                    style={{
                      padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                      borderBottom: '1px solid var(--border)', cursor: 'pointer',
                      borderLeft: `4px solid ${CATEGORY_COLOR[o.category] ?? '#64748b'}`,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <div style={{ width: 110, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{o.code}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{o.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {o.category_label} · {String(o.cadence_rule ?? '').replace(/_/g, ' ')}
                        {o.notes ? ` · ${o.notes}` : ''}
                      </div>
                    </div>
                    <div style={{ width: 120, textAlign: 'right', fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{fmtDate(o.due_date)}</div>
                      <div style={{
                        fontSize: 11, marginTop: 1,
                        color: days < 0 ? '#ef4444' : days <= 7 ? '#f59e0b' : 'var(--text-muted)',
                      }}>
                        {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'today' : `in ${days}d`}
                      </div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                );
              })}
          </div>
        </div>
      ))}

      {pushOpen && (
        <PushToStatePortalModal projectId={projectId} onClose={() => setPushOpen(false)} />
      )}
      {selected && (
        <div onClick={() => setSelected(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: 480, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace' }}>{selected.code}</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, lineHeight: 1.3 }}>{selected.name}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ marginTop: 12 }}><StatusBadge status={selected.status} /></div>
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Detail label="Category" value={selected.category_label} />
              <Detail label="Cadence" value={String(selected.cadence_rule ?? '').replace(/_/g, ' ')} />
              <Detail label="Due date" value={fmtDate(selected.due_date)} />
              <Detail label="Owner" value={selected.owner_role ?? '—'} />
              {selected.period_start && <Detail label="Period start" value={String(selected.period_start).slice(0, 10)} />}
              {selected.period_end &&   <Detail label="Period end"   value={String(selected.period_end).slice(0, 10)} />}
              <Detail label="Evidence type" value={selected.evidence_type ?? '—'} />
              <Detail label="Auto-generated" value={selected.auto_level} />
            </div>
            {selected.phase_id && phasesById.get(selected.phase_id) && (() => {
              const ph = phasesById.get(selected.phase_id);
              return (
                <div onClick={() => navigate('/construction')}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#eef2ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f5f3ff'}
                  style={{
                    marginTop: 16, padding: 12, background: '#f5f3ff',
                    borderRadius: 4, borderLeft: '4px solid #8b5cf6',
                    cursor: 'pointer', transition: 'background 0.12s',
                  }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Triggered by phase \u2192
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 600 }}>
                    {ph.template_code && <span style={{ color: '#94a3b8', marginRight: 6 }}>{ph.template_code}</span>}
                    {ph.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2 }}>Open in Construction \u2192</div>
                </div>
              );
            })()}

            {selected.source_cite && (
              <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 4, borderLeft: `4px solid ${CATEGORY_COLOR[selected.category] ?? '#64748b'}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Source citation</div>
                <div style={{ marginTop: 4, fontSize: 13 }}>{selected.source_cite}</div>
              </div>
            )}
            {selected.notes && (
              <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.05em' }}>Notes</div>
                <div style={{ marginTop: 4, fontSize: 13 }}>{selected.notes}</div>
              </div>
            )}
            <AutopilotPanel obligation={{ id: selected.id, code: selected.code, status: selected.status }} />
            <ObligationDrawerActions
              obligation={selected}
              projectId={projectId}
              onClose={() => setSelected(null)}
            />
          </div>
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------------------
// Drawer actions: status update + reviewer notes + attachments
// --------------------------------------------------------------------

type Attachment = {
  id: string;
  kind: string;
  url: string;
  filename: string | null;
  document_id: string | null;
  doc_filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  sha256: string | null;
  uploaded_at: string;
  notes: string | null;
};

const ATTACHMENT_KINDS = [
  { v: 'invoice',     l: 'Invoice' },
  { v: 'photo',       l: 'Photo' },
  { v: 'attestation', l: 'Attestation' },
  { v: 'form_pdf',    l: 'Form / PDF' },
  { v: 'log',         l: 'Log' },
  { v: 'other',       l: 'Other' },
];

const STATUS_OPTIONS = [
  { v: 'pending',     l: 'Pending' },
  { v: 'in_progress', l: 'In progress' },
  { v: 'ai_drafted',  l: '✦ AI-drafted' },
  { v: 'submitted',   l: 'Submitted' },
  { v: 'accepted',    l: 'Accepted' },
  { v: 'rejected',    l: 'Rejected' },
  { v: 'na',          l: 'N/A' },
];

const fmtBytesShort = (n: number | null) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1_048_576).toFixed(1)} MB`;
};

function ObligationDrawerActions({
  obligation, projectId, onClose,
}: {
  obligation: Obligation;
  projectId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(obligation.status);
  const [notes, setNotes] = useState(obligation.notes ?? '');
  const [reviewerNotes, setReviewerNotes] = useState((obligation as any).reviewer_notes ?? '');
  const [rejectedReason, setRejectedReason] = useState((obligation as any).rejected_reason ?? '');
  const [attachKind, setAttachKind] = useState('form_pdf');
  const [pendingDocId, setPendingDocId] = useState<string | null>(null);

  const { data: attData } = useQuery<{ items: Attachment[] }>({
    queryKey: ['obligation-attachments', obligation.id],
    queryFn: () => api.get(`/bead-pm/obligations/${obligation.id}/attachments`),
  });
  const attachments: Attachment[] = attData?.items ?? [];

  const updateMut = useMutation({
    mutationFn: (body: any) => api.put(`/bead-pm/obligations/${obligation.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obligations', projectId] });
      qc.invalidateQueries({ queryKey: ['compliance-summary', projectId] });
    },
  });
  const linkAttMut = useMutation({
    mutationFn: (b: { document_id: string; kind: string }) =>
      api.post(`/bead-pm/obligations/${obligation.id}/attachments`, b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obligation-attachments', obligation.id] });
      setPendingDocId(null);
    },
  });
  const unlinkAttMut = useMutation({
    mutationFn: (aid: string) =>
      api.del(`/bead-pm/obligations/${obligation.id}/attachments/${aid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['obligation-attachments', obligation.id] }),
  });

  // When the user picks a document in the uploader, immediately link it.
  useEffect(() => {
    if (pendingDocId) {
      linkAttMut.mutate({ document_id: pendingDocId, kind: attachKind });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDocId]);

  const dirty = status !== obligation.status
    || notes !== (obligation.notes ?? '')
    || reviewerNotes !== ((obligation as any).reviewer_notes ?? '')
    || rejectedReason !== ((obligation as any).rejected_reason ?? '');

  function save() {
    const body: any = {};
    if (status !== obligation.status) body.status = status;
    if (notes !== (obligation.notes ?? '')) body.notes = notes;
    if (reviewerNotes !== ((obligation as any).reviewer_notes ?? '')) body.reviewer_notes = reviewerNotes;
    if (rejectedReason !== ((obligation as any).rejected_reason ?? '')) body.rejected_reason = rejectedReason;
    updateMut.mutate(body, { onSuccess: () => onClose() });
  }

  return (
    <>
      {/* Status updater */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
          Update status
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>

      {/* Notes */}
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
          Internal notes
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What got done, what's blocking, etc."
          style={{ width: '100%', minHeight: 60, padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
        />
      </div>

      {status === 'rejected' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
            Rejection reason
          </div>
          <textarea
            value={rejectedReason}
            onChange={(e) => setRejectedReason(e.target.value)}
            placeholder="State's stated reason for rejection — verbatim."
            style={{ width: '100%', minHeight: 50, padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit' }}
          />
        </div>
      )}

      {(status === 'submitted' || status === 'accepted') && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
            Reviewer notes
          </div>
          <textarea
            value={reviewerNotes}
            onChange={(e) => setReviewerNotes(e.target.value)}
            style={{ width: '100%', minHeight: 50, padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit' }}
          />
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={save} disabled={!dirty || updateMut.isPending}
          style={{ padding: '6px 14px', background: dirty ? '#0f172a' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: dirty ? 'pointer' : 'not-allowed' }}
        >{updateMut.isPending ? 'Saving…' : 'Save changes'}</button>
      </div>

      {/* Attachments */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
          Evidence attachments ({attachments.length})
        </div>
        {attachments.length > 0 && (
          <div style={{ marginBottom: 12, border: '1px solid var(--border)', borderRadius: 4 }}>
            {attachments.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <span style={{ fontSize: 14 }}>📎</span>
                <a href={a.url} target="_blank" rel="noreferrer"
                  style={{ flex: 1, color: '#0f172a', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.doc_filename || a.filename || 'untitled'}
                </a>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{a.kind}</span>
                {a.size_bytes && <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtBytesShort(a.size_bytes)}</span>}
                <button onClick={() => { if (confirm('Remove this attachment?')) unlinkAttMut.mutate(a.id); }}
                  style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 14, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
          <select value={attachKind} onChange={(e) => setAttachKind(e.target.value)}
            style={{ padding: '6px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }}>
            {ATTACHMENT_KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
          </select>
          <DocumentUploader
            value={null}
            onChange={(id) => { if (id) setPendingDocId(id); }}
            projectId={projectId}
            docType={`obligation_${obligation.code}`}
            hint="Drop the evidence file. Linked automatically once uploaded."
          />
        </div>
      </div>
    </>
  );
}
